import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Core Advantages data (Japanese)
// ─────────────────────────────────────────────
const coreAdvantages = [
  {
    icon: 'ri-leaf-line',
    tag: '01',
    title: '天然原料へのこだわり',
    subtitle: '天然食物由来の栄養素を採用 — 健康的でストレスフリー',
    desc: '康永生技は天然食物由来の原料を厳選し、過度に精製された成分や合成添加物を避け、すべての処方を自然本来の姿に近づけています。原料産地から成分純度まで全過程を透明に開示することで、消費者は安心して摂取でき、ブランドは自信を持って販売できます。',
    points: [
      '天然原料サプライヤーの資格を厳格に審査',
      'バッチごとに農薬残留・重金属検査報告書を添付',
      '不必要な合成添加物は一切使用しない',
      'ご要望に応じてオーガニック認定原料も選択可能',
    ],
    img: 'https://readdy.ai/api/search-image?query=fresh%20natural%20herbs%20botanical%20ingredients%20turmeric%20ginger%20green%20tea%20extract%20dried%20herbs%20arranged%20beautifully%20on%20white%20marble%20surface%2C%20health%20supplement%20natural%20raw%20materials%2C%20clean%20minimal%20professional%20photography%20with%20soft%20natural%20lighting&width=720&height=480&seq=ca-natural-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-heart-line',
    tag: '02',
    title: '健康の三方よし',
    subtitle: 'クライアントの市場参入を支援し、消費者が健康を育める環境をつくる',
    desc: '優れたOEMパートナーは三方よしを実現できると私たちは信じています。ブランドクライアントが成功裏に製品を上市して利益を上げ、エンドユーザーが本当に効果的な健康食品を手にし、康永生技が専門技術を磨き続ける — この三者は相互に依存し、どれ一つ欠けても成り立ちません。',
    points: [
      'ブランドクライアント：健康市場への参入障壁とリスクを低減',
      '消費者：成分が透明で品質が安定した健康食品を入手',
      '康永：すべての協業を通じて技術と実績を積み上げる',
      '長期的なパートナーシップで共に成長し、成果を分かち合う',
    ],
    img: 'https://readdy.ai/api/search-image?query=happy%20healthy%20diverse%20group%20of%20people%20holding%20supplement%20products%20with%20green%20nature%20background%2C%20health%20wellness%20teamwork%20collaboration%20concept%2C%20warm%20natural%20lighting%20professional%20photography&width=720&height=480&seq=ca-winwin-01&orientation=landscape',
    reverse: true,
  },
  {
    icon: 'ri-service-line',
    tag: '03',
    title: '統合型プロフェッショナルサービス',
    subtitle: '6つのシンプルなステップで理想の製品を実現 — 低リスクな天然健康食品へ',
    desc: '初回コンサルティングから市場投入まで、康永はワンストップの統合サービスを提供します。複数の業者と個別に連絡を取る必要はなく、1つの窓口で処方開発・製造・品質管理・パッケージデザインまで完結。コミュニケーションコストとリスクを大幅に削減します。',
    points: [
      'ステップ1：ニーズ相談・製品ポジショニング分析',
      'ステップ2：処方開発・原料選定',
      'ステップ3：小ロット試作・サンプル確認',
      'ステップ4：品質管理検収・法規審査',
      'ステップ5：量産製造・パッケージング・出荷',
      'ステップ6：上市後テクニカルサポート',
    ],
    img: 'https://readdy.ai/api/search-image?query=professional%20biotech%20team%20meeting%20discussing%20product%20development%20roadmap%20with%20tablet%20laptop%20in%20modern%20clean%20office%2C%20health%20supplement%20company%20collaboration%2C%20natural%20light%20bright%20interior&width=720&height=480&seq=ca-service-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-store-line',
    tag: '04',
    title: '小ロット対応',
    subtitle: '在庫リスクを最小化 — ニッチ市場に最適な選択',
    desc: '多くの新興ブランドやニッチ市場クライアントが直面する最大の障壁は、最小発注数量（MOQ）が高すぎることです。康永は小ロット受注に対応しており、最小限の在庫リスクで市場ニーズを検証し、製品の実現可能性を確認してから量産へとスムーズに移行できます。',
    points: [
      'カプセル・錠剤：MOQ 3,000本から',
      '粉末スティック・顆粒：MOQ 5,000包から',
      '市場テスト中の新興ブランドやニッチアイテムに最適',
      '試作通過後は量産へシームレスに移行可能',
    ],
    img: 'https://readdy.ai/api/search-image?query=small%20batch%20supplement%20production%20sample%20bottles%20capsules%20arranged%20neatly%20on%20clean%20white%20table%2C%20minimal%20order%20quantity%20product%20testing%2C%20health%20product%20sampling%20photography&width=720&height=480&seq=ca-moq-01&orientation=landscape',
    reverse: true,
  },
];

// ─────────────────────────────────────────────
// Production data (Japanese)
// ─────────────────────────────────────────────
const envFeatures = [
  {
    icon: 'ri-building-4-line',
    title: 'クリーン生産エリア',
    desc: 'クラス10,000クリーンルーム基準を満たし、温湿度を常時監視。交差汚染を防ぎ、バッチごとの品質安定性を確保します。',
  },
  {
    icon: 'ri-flask-line',
    title: '自社研究開発ラボ',
    desc: '最新の分析機器を完備。処方開発・安定性試験・有効性検証を支援し、品質を源流から管理します。',
  },
  {
    icon: 'ri-test-tube-line',
    title: '品質管理検査室',
    desc: '微生物・重金属・農薬残留など完全な検査設備を備え、バッチごとにCoA合格検査報告書を発行します。',
  },
  {
    icon: 'ri-store-2-line',
    title: '温度管理倉庫・物流',
    desc: '原料と製品の特性に応じてゾーン別に温度管理。FIFO（先入れ先出し）管理で原料の鮮度と製品の安全性を確保します。',
  },
];

const qcProcess = [
  { step: '01', icon: 'ri-truck-line', title: '原料入荷検品', desc: 'バッチごとに外観・成分含量・微生物・重金属などの多項目検査を実施。不合格品は全量返品します。' },
  { step: '02', icon: 'ri-settings-3-line', title: '製造工程管理', desc: '生産全工程で重量差、崩壊度、硬度などの重要品質指標を監視。異常検知時は即座にライン停止します。' },
  { step: '03', icon: 'ri-eye-line', title: '半製品中間管理', desc: '各工程節点で中間品質チェックを実施。規格適合を確認してから次工程へ進めます。' },
  { step: '04', icon: 'ri-checkbox-circle-line', title: '完成品全項目検査', desc: '出荷前に微生物・農薬・重金属・表示適合性など完全な最終製品検査を実施します。' },
  { step: '05', icon: 'ri-file-check-line', title: '検査報告書発行', desc: 'バッチごとに完全なCertificate of Analysis（CoA）を発行。お客様の保管・市場審査に使用できます。' },
];

const certifications = [
  {
    name: 'GMP認証',
    fullName: '優良製造規範',
    desc: '台湾食品GMP優良製造規範に適合。生産環境・工程管理・品質文書すべてが認証基準に達しており、製造工程の信頼性を保証します。',
    icon: 'ri-award-line',
    color: '#4CAF50',
  },
  {
    name: 'HACCP認証',
    fullName: '危害分析重要管理点',
    desc: '体系的な危害分析プロセスを構築し、各重要管理点（CCP）に管理基準を設定して食品安全上の危害を積極的に予防します。',
    icon: 'ri-scan-line',
    color: '#388e3c',
  },
  {
    name: 'ISO 22000',
    fullName: '食品安全マネジメントシステム',
    desc: '国際食品安全マネジメントシステム認証を取得。HACCPの原則とISO管理システムを統合し、国際基準に準拠した食品安全を提供します。',
    icon: 'ri-shield-check-line',
    color: '#2e7d32',
  },
  {
    name: 'SGS第三者検査',
    fullName: '独立した第三者安全検証',
    desc: 'SGSなど国際的な第三者機関に定期的に独立検査を委託。客観的データで製品の安全性を実証し、ブランドの信頼性を高めます。',
    icon: 'ri-verified-badge-line',
    color: '#43a047',
  },
];

const docSupport = [
  { icon: 'ri-file-list-3-line', text: 'バッチ生産記録（Batch Record）' },
  { icon: 'ri-file-check-line', text: '完成品検査報告書（CoA）' },
  { icon: 'ri-file-paper-2-line', text: '原料規格書（Raw Material Spec）' },
  { icon: 'ri-shield-check-line', text: '安定性試験報告書' },
  { icon: 'ri-global-line', text: '輸出用品質文書サポート' },
  { icon: 'ri-building-line', text: '工場監査対応・資料提供' },
];

// ─────────────────────────────────────────────
// Cases data (Japanese)
// ─────────────────────────────────────────────
const cases = [
  {
    id: 1,
    tag: '美容・スキンケア',
    tagColor: '#4CAF50',
    title: '育毛・黒髪ゼリースティック製品',
    subtitle: '天然植物エキスを用いた、頭髪の健康をサポートする機能性ゼリースティックを開発',
    img: 'https://readdy.ai/api/search-image?query=hair%20health%20beauty%20supplement%20jelly%20stick%20product%20with%20natural%20plant%20extract%20black%20sesame%20ginger%20ingredients%20on%20clean%20white%20background%20elegant%20packaging%2C%20professional%20product%20photography&width=600&height=400&seq=case-hair-01&orientation=landscape',
    background: 'クライアントは頭髪ケアに特化したブランドで、美味しさと機能性を両立したゼリースティック剤型を開発したいというご要望でした。育毛・強髪・黒髪をコンセプトに、35〜60歳の薄毛に悩む消費者をターゲットとしていました。',
    challenge: '天然の黒髪成分（黒ごまや何首烏エキスなど）は独特の風味があり、味を損なわずに有効含量を確保する必要がありました。また、ゼリースティック剤型は均一性と安定性に高い要求があります。',
    solution: '康永の研究開発チームは複数回の風味調整を実施し、天然果汁で苦みをマスキング。安定剤でスティックごとの含量均一性を確保し、6ヶ月の加速安定性試験で保存期間を確認しました。',
    result: '製品は安定性検証を通過。ECプラットフォームでの発売後、初月の売上は予想を超え、リピート率も高く、追加注文で量産規模を拡大し続けています。',
  },
  {
    id: 2,
    tag: 'ペット健康',
    tagColor: '#388e3c',
    title: '国内外向けペット健康食品シリーズ',
    subtitle: '飼料添加物規制に準拠した、ペットのための天然機能性健康食品',
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20products%20natural%20organic%20dog%20cat%20vitamins%20treats%20on%20light%20background%2C%20cute%20animal%20care%20products%20with%20herbs%20botanical%20ingredients%2C%20professional%20product%20photography&width=600&height=400&seq=case-pet-02&orientation=landscape',
    background: 'クライアントはペット健康ブランドで、台湾国内市場と東南アジア輸出市場の両方を狙う計画でした。ペット飼料添加物関連法規に精通したOEM工場を必要としていました。',
    challenge: '台湾のペット飼料規制は一般食品規制と異なり、原料選択と表示形式に特別な注意が必要でした。東南亜アジア輸出では各国の現地規制への対応と二重の文書準備も求められました。',
    solution: '康永は専門の法規担当者をアサインし、原料のコンプライアンスを確認。台湾と各輸出先市場の規制に沿った処方文書を別々に準備し、関連申請手続きのサポートも行いました。',
    result: '製品シリーズが台湾で順調に発売され、同時にマレーシアとタイの輸入許可も取得。クライアントの主力商品となり、現在は6 SKUまで展開が拡大しています。',
  },
  {
    id: 3,
    tag: '子ども栄養',
    tagColor: '#2e7d32',
    title: '子ども向け栄養補助粉末',
    subtitle: '子ども向けに開発した、美味しくて安全な成分の粉末補助食品',
    img: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20powder%20nutrition%20stick%20packet%20colorful%20fruit%20flavored%20with%20vitamins%20minerals%20on%20white%20background%2C%20kids%20health%20food%20product%20photography&width=600&height=400&seq=case-kids-01&orientation=landscape',
    background: 'クライアントは子どもの健康ブランドで、複合ビタミン・ミネラル粉末を開発したいとのご要望でした。水に溶けやすく、自然な甘さで、人工着色料不使用、3〜12歳向けの日常補助食品として展開する計画でした。',
    challenge: '子ども向け製品は原料の安全性に最高水準が求められ、すべての原料が子どもに適していることを確認する必要がありました。また、粉末剤型は良好な分散性と溶解性が必要で、沈殿による味への影響を防ぐ必要がありました。',
    solution: '子ども向け安全グレードの原料を厳選し、第三者機関に重金属と微生物の二重検査を委託。天然果汁粉末で風味を開発し、ターゲット年齢層の好みに合わせて複数回の味覚テストを実施しました。',
    result: '製品は食品安全検査を通過し、実店舗の薬局とECチャネルで順調に発売。複数の育児ブロガーによる推薦を受け、クライアントブランドのスター商品となりました。',
  },
  {
    id: 4,
    tag: 'ラジオ局コラボ',
    tagColor: '#43a047',
    title: 'ラジオ局との高品質フィッシュオイル',
    subtitle: '著名放送メディアと提携し、高純度Omega-3フィッシュオイルシリーズを開発',
    img: 'https://readdy.ai/api/search-image?query=premium%20omega3%20fish%20oil%20capsules%20high%20purity%20supplement%20bottle%20with%20ocean%20sea%20background%2C%20high%20quality%20health%20product%20professional%20photography%20clean%20white%20minimal&width=600&height=400&seq=case-fishoil-01&orientation=landscape',
    background: '健康意識の高いリスナーを多く持つ著名ラジオ局が、自社健康食品ブランドの立ち上げを決定。高純度EPA/DHA、重金属検証済みの高品質フィッシュオイルをフラッグシップ製品に選びました。',
    challenge: '知名度の高いラジオ局は品質要件が厳しく、完全な第三者検査報告書を提出できるパートナーが必要でした。また、放送番組のスケジュールに合わせて初回ロットの納期を遵守する必要がありました。',
    solution: 'TG型高濃縮フィッシュオイルを提供（TG型Omega-3含量85%以上）。SGSに完全な重金属・環境汚染物質検査を委託し、生産スケジュールを放送局のタイムラインに柔軟に調整しました。',
    result: '製品は予定通り発売。番組内でのプロモーション後に販売反響が大きく、初回ロットは完売後に迅速な追加注文が入り、ラジオ局との協業案件の中で最も成功したプライベートブランドプロジェクトとなりました。',
  },
  {
    id: 5,
    tag: '農業コラボ',
    tagColor: '#1b5e20',
    title: '農業特産品を活用した健康食品シリーズ',
    subtitle: '地元農業原料を高付加価値健康食品に転換し、地域特色ブランドを構築',
    img: 'https://readdy.ai/api/search-image?query=local%20agriculture%20specialty%20health%20supplement%20Taiwan%20indigenous%20ingredients%20taro%20sweet%20potato%20rice%20extract%20natural%20products%20on%20wooden%20table%2C%20farm%20to%20table%20health%20food%20concept%20photography&width=600&height=400&seq=case-agri-01&orientation=landscape',
    background: 'クライアントは地域農業と結びついた食品ブランドで、台湾の特産農産物（特定品種の米・茶葉・ウコンなど）を高付加価値健康食品に転換し、地域ブランドイメージを強化したいというご要望でした。',
    challenge: '農業原料の成分含量は収穫期や産地によって異なるため、標準化された原料規格の策定が必要でした。また、地元原料の健康訴求は食品表示規制に準拠し、効果の誇大表現がないようにする必要がありました。',
    solution: '康永がクライアントの原料規格書策定を支援し、農業パートナーとの安定した調達基準を設定。研究開発チームが農産物の特性に基づいて処方を設計し、法規要件に適合した健康訴求文案の作成もサポートしました。',
    result: '製品シリーズが成功裏に発売。農業ブランドのストーリーと組み合わせて地域のお土産品として人気を博し、チェーンスーパーの健康食品売場にも進出。農業原料の付加価値を3〜5倍に向上させることに成功しました。',
  },
];

// ─────────────────────────────────────────────
// Regulation data (Japanese)
// ─────────────────────────────────────────────
const whyPoints = [
  {
    icon: 'ri-alert-line',
    title: '違反罰則の深刻さ',
    desc: '健康食品の表示や広告法規に違反すると、100万元以上の罰金が科せられ、製品の市場回収を命じられ、ブランド評判が著しく損なわれる可能性があります。',
  },
  {
    icon: 'ri-store-line',
    title: '販路開拓の審査要件',
    desc: 'チェーン薬局・スーパー・ECプラットフォームは製品審査時に完全なコンプライアンス文書の提出を求めます。規制不適合では上場できません。',
  },
  {
    icon: 'ri-global-line',
    title: '輸出先ごとに異なる法規',
    desc: '国によって健康食品の分類・原料安全性・表示形式の規定が異なります。輸出前には各ターゲット市場の規制を一つひとつ確認する必要があります。',
  },
  {
    icon: 'ri-time-line',
    title: '頻繁な法規改正',
    desc: '台湾のTFDAは原料ポジティブリスト・表示規範・広告審査基準を定期的に更新します。即時対応できないと、既存製品が規制不適合になる可能性があります。',
  },
];

const keyRegulations = [
  {
    title: '健康食品法規',
    items: [
      '健康食品認証申請（グリーンマーク）の条件と流れ',
      '一般食品による特定保健機能訴求の禁止',
      '原料ポジティブリスト：使用可能・不可能な成分',
      '1日摂取量上限と安全性評価の要件',
    ],
  },
  {
    title: '表示・広告基準',
    items: [
      '製品名・成分・添加物の表示形式',
      '訴求表現の合法的範囲（疾病予防・治療の主張は不可）',
      '広告媒体公開前の審査手続き',
      'SNS・KOL協業に関する広告規制の定義',
    ],
  },
  {
    title: '製造・品質管理法規',
    items: [
      'GMP優良製造規範の適用範囲',
      'HACCP危害分析の構築要件',
      '製品バッチ記録とトレーサビリティシステムの規範',
      '保存期限試験と表示方法',
    ],
  },
  {
    title: '輸出法規の差異',
    items: [
      '東南アジア（マレーシア・タイ・ベトナム）の輸入規制',
      '中国保健食品（青帽）申請フロー',
      '米国FDA栄養補助食品（DSHEA）規制',
      'EU Novel Food新奇食品申請',
    ],
  },
];

const howWeHelp = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: '事前法規審査',
    desc: '処方確定前に法規担当者が原料のコンプライアンス審査を実施し、すべての成分がTFDAのポジティブリストと使用量規範に適合していることを源流から確認します。',
  },
  {
    step: '02',
    icon: 'ri-file-list-3-line',
    title: '表示文書サポート',
    desc: '表示形式の推奨と文案審査サービスを提供し、製品名・成分表示・訴求表現の規制適合をサポート。広告違反を防止します。',
  },
  {
    step: '03',
    icon: 'ri-building-2-line',
    title: '健康食品申請サポート',
    desc: '健康食品認証（グリーンマーク）申請を希望するクライアントには、安全性・有効性評価に必要な科学文書と試験データの準備をサポートします。',
  },
  {
    step: '04',
    icon: 'ri-global-line',
    title: '輸出法規コンサルティング',
    desc: '輸出を計画するクライアントに対し、ターゲット市場の法規差異分析と各市場に必要な輸入文書・成分申告資料の準備をサポートします。',
  },
];

const faqs = [
  {
    q: '一般食品と健康食品の法規の違いは何ですか？',
    a: '一般食品は「健康の維持」に関する機能性表現のみ使用でき、特定の保健機能を訴求することは禁止されています。健康食品（グリーンマーク）はTFDAの審査を通過しており、「血中脂質の改善に役立つ」などの特定保健機能を合法的に訴求できます。申請の敷居・費用・審査期間は両者で大きく異なります。',
  },
  {
    q: '製品文案でどのような表現に注意が必要ですか？',
    a: '台湾の規制では、一般食品による「疾病の予防・治療・軽減・診断」に関する表現を明確に禁止しています。例えば「降血圧」「不眠治療」「糖尿病改善」などです。「正常な代謝の維持に役立つ」「体質調整を助ける」などの適法な表現への変更をお勧めします。',
  },
  {
    q: '輸入原料はすべて使用できますか？',
    a: 'すべての輸入原料が合法的に使用できるわけではありません。TFDAは「食品に使用可能な原料まとめ」のポジティブリストを公表しており、リストにない原料は安全性評価資料の提出が必要です。康永は処方開発前にすべての原料のコンプライアンスを確認します。',
  },
  {
    q: 'SNS広告の規制制限は何ですか？',
    a: '食品安全衛生管理法により、インターネット・SNS広告も食品広告規制の適用対象です。KOLやインフルエンサーとの協業において、具体的な効果や使用体験を言及すれば広告と見なされます。誇大・不実な内容があった場合、ブランド側と広告発信者の両者が処罰される可能性があります。',
  },
  {
    q: '東南アジアへの輸出には追加の認証が必要ですか？',
    a: 'はい、各国で要件が異なります。マレーシアはKKMへの輸入許可申請、タイはタイFDAへの登録、ベトナムは保健省の輸入許可書が必要です。康永はこれらの文書準備をサポートした実績があり、初期コンサルティングを提供できます。',
  },
];

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function JaAboutSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/ja/about/core-advantages': {
      title: 'コア強み｜天然素材・小ロット対応・統合サービス｜康永生技',
      description: '康永生技の4つのコア強み：天然原料へのこだわり、健康の三方よし、統合型プロフェッショナルサービス、小ロット対応。ブランドが健康食品市場に参入しやすい環境を提供します。',
      keywords: 'コア強み,天然原料,小ロット,OEMサービス,三方よし,統合サービス',
    },
    '/ja/about/production': {
      title: '専門生産｜GMP認定工場・5段階品質管理｜康永生技',
      description: '康永生技のGMP認定生産環境：クリーンルーム、研究開発ラボ、品質管理検査室、温度管理倉庫。5段階品質管理フローで各ロットの安全性と適合性を確保します。',
      keywords: 'GMP工場,専門生産,品質管理,クリーンルーム,健康食品製造,ISO 22000',
    },
    '/ja/about/cases': {
      title: '成功事例｜OEM ODM代工実績｜康永生技',
      description: '康永生技のOEM成功事例：美容スキンケア、ペット健康、子ども栄養、ラジオ局コラボ、農業コラボなど多様な製品カテゴリーの製造実績。',
      keywords: '成功事例,OEM事例,ODM事例,健康食品製造事例,ブランド協業',
    },
    '/ja/about/regulation': {
      title: '法規リスク管理｜健康食品表示コンプライアンス｜康永生技',
      description: '康永生技の法規リスク管理サービス：上市前コンプライアンス審査、表示文書サポート、健康食品認証申請支援、輸出法規コンサルティング。',
      keywords: '健康食品法規,表示適合,食品安全法規,TFDA,健康食品認証,輸出法規',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/ja/about/core-advantages'];
  useSEO({ ...currentSeo, lang: 'ja', canonicalPath: path });

  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <JaLayout>
      {/* ── コア強み ── */}
      {path === '/ja/about/core-advantages' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=lush%20green%20botanical%20garden%20overhead%20view%20with%20diverse%20natural%20plants%20herbs%20growing%2C%20vibrant%20emerald%20green%20nature%20background%2C%20health%20supplement%20brand%20identity%2C%20serene%20clean%20natural%20environment&width=1920&height=420&seq=ca-hero-bg-01&orientation=landscape"
                alt="コア強み"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/about" className="hover:text-white cursor-pointer">康永について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">コア強み</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">康永について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">コア強み</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  天然原料へのこだわり・健康の三方よし・統合型プロフェッショナルサービス・小ロット対応の4つの強みで、ブランドが健康食品市場に安定して参入できるよう支援します。
                </p>
              </div>
            </div>
          </section>

          {/* Advantage blocks */}
          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-14 md:space-y-24">
              {coreAdvantages.map((adv) => (
                <div
                  key={adv.tag}
                  className={`flex flex-col ${adv.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}
                >
                  <div className="w-full lg:w-1/2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[#4CAF50] font-bold text-[2rem] leading-none opacity-20">{adv.tag}</span>
                      <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl border border-green-200">
                        <i className={`${adv.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] leading-tight mb-2">{adv.title}</h2>
                    <p className="text-[#4CAF50] font-medium text-[1rem] mb-5">{adv.subtitle}</p>
                    <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{adv.desc}</p>
                    <ul className="space-y-3">
                      {adv.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-2xl overflow-hidden">
                      <img src={adv.img} alt={adv.title} className="w-full h-[300px] lg:h-[360px] object-cover object-top" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">パートナーシップを始める</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-4">これらの強みをあなたのために活用しませんか？</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                康永生技の営業チームにご連絡ください。最適なODM協業プランをご提案します。
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  ODM協業についてお問い合わせ
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/ja/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  協業フローを確認する
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">「康永について」の関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: '専門生産', desc: 'GMP工場・5段階品管・国際認証でブランドに最高水準の生産保証を提供します。', href: '/ja/about/production' },
                  { title: '成功事例', desc: '多様な品目の全行程代行事例 — 康永がどのようにブランドの上市を支援するかをご覧ください。', href: '/ja/about/cases' },
                  { title: '協業フロー', desc: 'ニーズ確認から納品まで、協業の各ステップを明確に把握できます。', href: '/ja/cooperation/process' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── 専門生産 ── */}
      {path === '/ja/about/production' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20GMP%20certified%20pharmaceutical%20manufacturing%20production%20line%20stainless%20steel%20equipment%20workers%20white%20uniform%20hairnet%20clean%20room%2C%20professional%20health%20supplement%20factory%20interior%20bright%20lighting&width=1920&height=420&seq=prod-hero-bg-01&orientation=landscape"
                alt="専門生産"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/about" className="hover:text-white cursor-pointer">康永について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">専門生産</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">康永について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">専門生産</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  安定した製造工程・品質管理・食品安全認証により、ブランドが信頼できる安心な健康食品を作り上げられるよう支援します。
                </p>
              </div>
            </div>
          </section>

          {/* Production environment */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">生産施設</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">プロフェッショナルな生産環境</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  康永生技の工場はGMP規格基準に達しており、すべての製品に最も安全な生産環境を提供します
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
                {envFeatures.map((f) => (
                  <div key={f.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                      <i className={`${f.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{f.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20clean%20pharmaceutical%20supplement%20manufacturing%20facility%20with%20modern%20equipment%20conveyors%20stainless%20steel%20tanks%20workers%20in%20white%20uniforms%2C%20GMP%20factory%20interior%20bright%20professional%20environment&width=1400&height=500&seq=prod-facility-wide-01&orientation=landscape"
                  alt="康永生技工場全景"
                  className="w-full h-[320px] md:h-[420px] object-cover object-center"
                />
              </div>
            </div>
          </section>

          {/* QC Process */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">品質管理フロー</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">5段階品質管理フロー</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  原料入荷から完成品出荷まで、すべての工程で厳格な品質管理メカニズムが機能します
                </p>
              </div>
              <div className="relative">
                <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-green-100 z-0" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
                  {qcProcess.map((q) => (
                    <div key={q.step} className="flex flex-col items-center text-center">
                      <div className="relative mb-5">
                        <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full border-2 border-[#4CAF50]">
                          <i className={`${q.icon} text-[#4CAF50] text-2xl`} />
                        </div>
                        <span className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{q.step}</span>
                      </div>
                      <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{q.title}</h3>
                      <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{q.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">認証資格</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">国際的に認められた品質認証</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  HACCP・ISO22000・GMPなど複数の認証が、康永生技の品質へのコミットメントを最も力強く裏付けています
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certifications.map((cert) => (
                  <div key={cert.name} className="flex gap-5 p-7 bg-[#f7faf7] rounded-2xl border border-gray-100 hover:border-green-200 transition-all duration-300">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${cert.color}15` }}>
                      <i className={`${cert.icon} text-2xl`} style={{ color: cert.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{cert.name}</h3>
                        <span className="text-gray-400 text-[0.75rem]">/ {cert.fullName}</span>
                      </div>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{cert.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Document support */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">文書サポート</span>
                  <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                    生産証明と品質文書の<br />全方位サポート
                  </h2>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                    康永生技は完全な品質文書サポートを提供し、クライアントが市場審査・販路審査・輸出通関のニーズに対応できるよう支援します。ブランドがより遠くへ、より安定して進めるようにサポートします。
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {docSupport.map((d) => (
                      <div key={d.text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <i className={`${d.icon} text-[#4CAF50] text-lg`} />
                        </div>
                        <span className="text-[#1a2e1a] text-[0.875rem] font-medium">{d.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=quality%20control%20manager%20reviewing%20product%20batch%20records%20certificates%20documents%20in%20clean%20pharmaceutical%20laboratory%20office%2C%20professional%20QA%20QC%20documentation%20management%20biotech%20company&width=720&height=480&seq=prod-doc-01&orientation=landscape"
                      alt="品質文書サポート"
                      className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ODM/OEM CTA */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-[#f0faf0] border border-green-100 rounded-3xl px-8 lg:px-14 py-12 text-center">
                <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">ODM / OEM 対応</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                  康永の専門生産能力を<br />あなたのブランドの強力な後ろ盾に
                </h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                  ODMカスタム処方受託製造でも、OEM指定処方製造でも、康永は最高品質基準ですべての委託を完成させます。
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    ODM / OEM 協業のお問い合わせ
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <Link to="/ja/about/core-advantages" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    コア強みを確認する
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">「康永について」の関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'コア強み', desc: '天然原料・三方よし・統合サービス・小ロット対応の4つのコア強みを詳しく解説。', href: '/ja/about/core-advantages' },
                  { title: '法規リスク', desc: '健康食品法規の要点を把握し、康永があなたのブランドの規制遵守をサポートします。', href: '/ja/about/regulation' },
                  { title: 'お問い合わせ', desc: 'お問い合わせフォームを記入して、康永がどのように生産サポートを提供できるか確認しましょう。', href: '/ja/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── 成功事例 ── */}
      {path === '/ja/about/cases' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20collection%20of%20health%20supplement%20products%20bottles%20capsules%20powders%20lined%20up%20on%20clean%20white%20background%2C%20professional%20health%20brand%20showcase%20product%20photography%2C%20natural%20light&width=1920&height=420&seq=cases-hero-bg-01&orientation=landscape"
                alt="成功事例"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/about" className="hover:text-white cursor-pointer">康永について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">成功事例</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">康永について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">成功事例</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  多様な製品カテゴリー、豊富なOEM実績 — 以下は康永生技の代表的な協業事例の一部です。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 bg-white border-b border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { num: '600+', label: 'サービス提供ブランド数' },
                  { num: '3,200+', label: '累計OEM品目数' },
                  { num: '15+', label: '製品カテゴリー' },
                  { num: '10+', label: '輸出ターゲット市場' },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-[#4CAF50] font-bold text-[2rem] md:text-[2.5rem] leading-none mb-1">{s.num}</p>
                    <p className="text-gray-500 text-[0.875rem]">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cases list */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">OEM事例</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">厳選された協業事例</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  事例をクリックすると、協業背景・課題・成果の詳細情報が展開されます
                </p>
              </div>
              <div className="space-y-5">
                {cases.map((c) => (
                  <div
                    key={c.id}
                    className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${expandedCase === c.id ? 'border-[#4CAF50]/40' : 'border-gray-100 hover:border-green-200'}`}
                  >
                    <button
                      className="w-full text-left cursor-pointer"
                      onClick={() => setExpandedCase(expandedCase === c.id ? null : c.id)}
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 p-6">
                        <div className="w-full sm:w-[160px] h-[120px] sm:h-[100px] flex-shrink-0 rounded-xl overflow-hidden">
                          <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="inline-block text-[0.75rem] font-medium px-3 py-1 rounded-full text-white" style={{ backgroundColor: c.tagColor }}>{c.tag}</span>
                            <span className="text-gray-300 text-xs">事例 0{c.id}</span>
                          </div>
                          <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-1">{c.title}</h3>
                          <p className="text-gray-500 text-[0.875rem] leading-[1.6] line-clamp-2">{c.subtitle}</p>
                        </div>
                        <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${expandedCase === c.id ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                          <i className={`text-sm ${expandedCase === c.id ? 'ri-subtract-line' : 'ri-add-line'}`} />
                        </div>
                      </div>
                    </button>
                    {expandedCase === c.id && (
                      <div className="px-6 pb-7 border-t border-gray-100 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {[
                            { label: '協業背景', icon: 'ri-information-line', content: c.background },
                            { label: '主な課題', icon: 'ri-error-warning-line', content: c.challenge },
                            { label: '康永のサポート内容', icon: 'ri-tools-line', content: c.solution },
                            { label: '協業成果', icon: 'ri-trophy-line', content: c.result },
                          ].map((block) => (
                            <div key={block.label} className="bg-[#f7faf7] rounded-xl p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-7 h-7 flex items-center justify-center">
                                  <i className={`${block.icon} text-[#4CAF50] text-base`} />
                                </div>
                                <h4 className="text-[#1a2e1a] font-bold text-[0.9375rem]">{block.label}</h4>
                              </div>
                              <p className="text-gray-600 text-[0.875rem] leading-[1.75]">{block.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                あなたのブランドも、次の成功事例になれます
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                製品アイデアの規模を問わず、康永生技はすべての協業機会に真摯に向き合います。
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  お問い合わせ・協業開始
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/ja/services" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  OEMサービスを見る
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">「康永について」の関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'コア強み', desc: '天然原料・三方よし・統合サービス・小ロット対応の4つのコア強みをご確認ください。', href: '/ja/about/core-advantages' },
                  { title: '協業フロー', desc: 'ニーズ確認から納品まで、協業の各ステップを明確に把握できます。', href: '/ja/cooperation/process' },
                  { title: 'お問い合わせ', desc: 'お問い合わせフォームを記入して、康永が次の上市ブランドを支援します。', href: '/ja/cooperation/inquiry' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── 法規リスク管理 ── */}
      {path === '/ja/about/regulation' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20legal%20document%20review%20compliance%20health%20supplement%20regulation%20papers%20on%20clean%20desk%20with%20pen%20calculator%2C%20food%20safety%20regulatory%20affairs%20concept%2C%20clean%20minimal%20office%20environment&width=1920&height=420&seq=reg-hero-bg-01&orientation=landscape"
                alt="法規リスク管理"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/68 to-[#1a3a1a]/30" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/about" className="hover:text-white cursor-pointer">康永について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">法規リスク管理</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">康永について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">法規リスク管理</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  製品上市前の法規チェックとリスク管理は、ブランドが長期的に安定経営できるかどうかの重要な基盤です。
                </p>
              </div>
            </div>
          </section>

          {/* Why regulation matters */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">なぜ法規が最優先なのか</span>
                  <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                    法規は「後工程の問題」ではなく、<br />製品の最前線に置くべきものです
                  </h2>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                    多くのブランドが製品開発完了後、あるいは上市後に法規問題を発見します。軽ければ表示の修正、重ければ市場回収となり、すべての先行投資が無駄になります。法規審査を製品開発の最初のステップに置くことで、すべてのリソースを効果的に活用できます。
                  </p>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                    康永生技はすべての協業開始時に法規事前審査を行い、製品の方向性・原料選択・訴求表現が生産に入る前から規制に適合していることを確認します。あなたのブランドが最初から適法な基盤に立てるよう支援します。
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {whyPoints.map((p) => (
                      <div key={p.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                          <i className={`${p.icon} text-[#4CAF50] text-base`} />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-0.5">{p.title}</p>
                          <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{p.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=regulatory%20affairs%20specialist%20reviewing%20food%20supplement%20compliance%20documents%20checking%20ingredient%20list%20with%20magnifying%20glass%20in%20office%2C%20professional%20health%20product%20regulatory%20review%20process&width=720&height=500&seq=reg-why-01&orientation=landscape"
                      alt="法規審査"
                      className="w-full h-[340px] lg:h-[420px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key regulations */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">法規要点</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">健康食品ブランドが知るべき重要法規</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  以下はブランドが製品企画時に最もよく直面する法規の側面をまとめたものです
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {keyRegulations.map((reg, idx) => (
                  <div key={reg.title} className="bg-white rounded-2xl p-7 border border-gray-100">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">0{idx + 1}</span>
                      <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{reg.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {reg.items.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-base" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How we help */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">康永のサポート</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">康永がどのように法規上のリスクを回避するか</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  処方設計から広告文案まで、康永はすべての重要チェックポイントで法規管理サービスを提供します
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {howWeHelp.map((h) => (
                  <div key={h.step} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">{h.step}</span>
                      <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100">
                        <i className={`${h.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-3">{h.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{h.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand guarantee */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  <div className="w-full lg:w-1/2">
                    <img
                      src="https://readdy.ai/api/search-image?query=business%20team%20shaking%20hands%20successful%20partnership%20agreement%20health%20supplement%20brand%20collaboration%2C%20confident%20professional%20people%20in%20clean%20bright%20modern%20office%2C%20trust%20partnership%20concept&width=720&height=460&seq=reg-guarantee-01&orientation=landscape"
                      alt="康永リスク保証"
                      className="w-full h-[280px] lg:h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-12">
                    <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">ブランド保証</span>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                      康永の法規サポートは<br />あなたのブランドに何をもたらすか？
                    </h2>
                    <ul className="space-y-4">
                      {[
                        { icon: 'ri-shield-check-line', text: '上市前にコンプライアンスを確認し、規制当局による処罰リスクを低減' },
                        { icon: 'ri-store-line', text: '販路審査基準を満たし、スムーズな上場資格取得' },
                        { icon: 'ri-global-line', text: '輸出文書が整備され、海外市場参入効率を向上' },
                        { icon: 'ri-time-line', text: '法規変更をリアルタイムで通知し、製品のコンプライアンスを維持' },
                        { icon: 'ri-hand-coin-line', text: '法規問題による市場撤退損失とリコールコストを回避' },
                        { icon: 'ri-award-line', text: '完全な品質文書により消費者からのブランド信頼性を向上' },
                      ].map((item) => (
                        <li key={item.text} className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className={`${item.icon} text-[#4CAF50] text-base`} />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-12">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">よくある質問</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">法規に関するよくある質問</h2>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className={`bg-[#f7faf7] rounded-xl border transition-all duration-200 overflow-hidden ${openFaq === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}
                  >
                    <button
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaq === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaq === idx ? 'bg-[#4CAF50] text-white' : 'bg-white text-[#4CAF50]'}`}>
                        <i className={`text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </div>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">パートナーシップを始める</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                康永が法規準備をサポートし、<br />安心して上市・長期経営できる体制を整えます
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                康永生技へのお問い合わせをお待ちしています。初期の法規実現可能性評価を行い、あなたのブランドが最初から正しい道を歩めるようサポートします。
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  法規実現可能性について相談
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/ja/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  協業フローを確認する
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">「康永について」の関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'パートナーシップサービス', desc: '法規リスク管理・マーケティング文案・輸入原料代理調達など、康永の全方位サービスをご確認ください。', href: '/ja/cooperation/services' },
                  { title: 'パッケージング', desc: 'パッケージ材料選択・ビジュアルデザイン・法規表示まで、ワンストップのパッケージングソリューション。', href: '/ja/services/packaging' },
                  { title: 'お問い合わせ', desc: 'お問い合わせフォームを記入して、康永が初期の法規実現可能性評価をサポートします。', href: '/ja/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </JaLayout>
  );
}
